import { NavArrowRight as CaretRightIcon } from 'iconoir-react'
import { ReactNode, useState } from 'react'
import { Button } from 'app/components/ui/button'
import { TableHead, TableHeader, TableRow } from 'app/components/ui/table'
import { cn } from 'app/lib/utils'

// Grouped-table styling shared with the Integrations Locations page
// (IntegrationsLocationsGroupedComponent): a standalone column-header card at
// the top, then per-group sections — a detached header row (caret + semibold
// label + subtle count) above an elevated rounded card holding a headerless
// table. Header and group tables share a fixed column layout via
// <GroupedTable> so their columns stay aligned.

export type GroupedTableColumn = {
  label: string
  /** Width class for the <col> (omit for an equal share of the remaining width). */
  colClassName?: string
  /** Extra classes for the header cell (e.g. `text-right` for an actions column). */
  headClassName?: string
}

/** Body-cell classes matching the Integrations grouped-table rows. */
export const GROUPED_TABLE_CELL = `px-5 py-3.5 text-body-sm`

/** Fixed-layout table shell shared by the header card and each group's body table. */
export function GroupedTable({ columns, children }: { columns: GroupedTableColumn[]; children: ReactNode }) {
  return (
    <table className="w-full table-fixed caption-bottom text-sm">
      <colgroup>
        {columns.map((c) => (
          <col key={c.label} className={c.colClassName} />
        ))}
      </colgroup>
      {children}
    </table>
  )
}

/** Standalone column-header card rendered once above all groups; sticks to the top of the page scroll. */
export function GroupedTableHeaderCard({ columns }: { columns: GroupedTableColumn[] }) {
  return (
    // -mt-3/pt-3 cancel out at rest; while stuck, the white top padding keeps a
    // clean gap between the pinned card and the page-header divider (rows
    // scrolling by are hidden behind the padding's background).
    <div className="sticky top-0 z-10 -mt-3 w-full bg-surface pt-3">
      <div className="w-full rounded-md border border-default bg-surface">
        <GroupedTable columns={columns}>
          <TableHeader>
            <TableRow className="bg-surface-subtle hover:bg-surface-subtle">
              {columns.map((c) => (
                <TableHead key={c.label} className={cn(`px-5 text-ui-xs font-bold text-default`, c.headClassName)}>
                  {c.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
        </GroupedTable>
      </div>
    </div>
  )
}

export type GroupedTableSectionProps = {
  label: string
  count: number
  /** Marker-dot color class (e.g. `bg-yellow-500`), mirroring the Integrations status dots. */
  dotClassName?: string
  /** Uncontrolled initial state; remount (key change) to reset. */
  defaultOpen?: boolean
  /** Controlled mode: pass both `open` and `onOpenChange`. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  children: ReactNode
}

/** Collapsible group: detached header button above an elevated card. */
export function GroupedTableSection({
  label,
  count,
  dotClassName,
  defaultOpen = true,
  open,
  onOpenChange,
  children,
}: GroupedTableSectionProps) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen)
  const isControlled = open !== undefined
  const isOpen = isControlled ? open : internalOpen

  const toggle = () => {
    if (isControlled) onOpenChange?.(!isOpen)
    else setInternalOpen(!isOpen)
  }

  return (
    <div className="flex flex-col">
      <Button onClick={toggle} variant="icon" size="inline" className="flex h-9 w-full items-center justify-start">
        <CaretRightIcon
          className={cn(`h-3.5 w-3.5 text-subtle transition-transform`, isOpen && `rotate-90`)}
          strokeWidth={2.5}
        />
        {dotClassName != null && <span className={cn(`h-2 w-2 shrink-0 rounded-full`, dotClassName)} aria-hidden />}
        <span className="text-body-sm font-semibold tracking-tight text-strong">{label}</span>
        <span className="text-label-xs leading-none font-medium tabular-nums text-minimal">{count}</span>
      </Button>
      {isOpen && <div className="rounded-md border border-default bg-surface shadow-elevation-1">{children}</div>}
    </div>
  )
}
